import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { primaryImageStyles } from "@tIntermatic/Overrides/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    onImageClick?: () => void;
    fullPrimaryImageModalIsOpen?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    appState: state,
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    lastSliderElement?: InjectableCss;
    innerCircle?: InjectableCss;
    carouselSlideLast?: InjectableCss;
    modalGridContainer?: GridContainerProps;
    modalGridItem?: GridItemProps;
    modalCarouselSlideInner?: InjectableCss;
}

const getImageCss = (borderColor: string) => css`
    border: ${borderColor};
    cursor: pointer;
    justify-content: flex-start;
    &:hover {
        border: 1px solid #cccccc;
    }
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            css`
                height: 51px;
                width: 51px;
            `,
            css`
                height: 51px;
                width: 51px;
            `,
            css`
                height: 51px;
                width: 51px;
            `,
            css`
                height: 68px;
                width: 68px;
            `,
            css`
                height: 68px;
                width: 68px;
            `,
        ])}
    img {
        max-width: 100%;
        max-height: 100%;
        padding: 3px;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: {
        gap: 0,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 30px !important;
                    `,
                    css`
                        margin: 0 30px !important;
                    `,
                    css`
                        margin: 0 30px !important;
                    `,
                    css`
                        margin: 0 50px !important;
                    `,
                    css`
                        margin: 0 50px !important;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            min-width: 100%;
        `,
    },
    carouselContainer: {
        css: css``,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            min-width: 100%;
            justify-content: flex-start;
            @media all and (max-width: 992px) and (min-width: 0px) {
                justify-content: center;
            }
            transform: none !important;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            cursor: pointer;
            flex: 0 0 13.9%;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 3px 0;
        `,
    },

    modalCarouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 8px 0;
        `,
    },

    image: {
        css: getImageCss("1px solid #E5E5E5;"),
    },
    selectedImage: {
        css: getImageCss("2px solid #006BC0"),
    },

    lastSliderElement: {
        css: css`
            border: 1px solid #e5e5e5;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 51px;
                        width: 51px;
                    `,
                    css`
                        height: 51px;
                        width: 51px;
                    `,
                    css`
                        height: 51px;
                        width: 51px;
                    `,
                    css`
                        height: 68px;
                        width: 68px;
                    `,
                    css`
                        height: 68px;
                        width: 68px;
                    `,
                ])}
        `,
    },
    innerCircle: {
        css: css`
            height: 3px;
            width: 3px;
            background-color: black;
            border-radius: 50%;
            display: inline-block;
            margin: 2px;
        `,
    },

    carouselSlideLast: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 3px 0 4px;
            @media all and (max-width: 768px) and (min-width: 0px) {
                padding: 10px 3px 0 7px;
            }
        `,
    },

    modalGridContainer: {
        css: css`
            display: grid;
            grid-template-columns: auto auto auto auto auto auto;
            padding: 10px;
            align-self: center;
            @media all and (max-width: 576px) and (min-width: 0px) {
                grid-template-columns: auto auto auto;
                // width: 200px;
                margin: 0 auto;
                justify-content: center;
            }
        `,
    },
    modalGridItem: {
        css: css`
            background-color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(0, 0, 0, 0.8);
            padding: 20px;
            font-size: 30px;
            text-align: center;
        `,
    },
};

// primaryImageStyles.fullPrimaryImageModal = {
//     cssOverrides: {
//         scrim: css`
//             height: 100% !important;
//             overflow: auto;
//             position: absolute !important;
//             touch-action: auto;
//         `,
//         modalContainer: css`
//             width: auto;
//             > div {
//                 @media all and (max-width: 768px) and (min-width: 0px) {
//                     min-width: 100%;
//                 }
//                 @media all and (max-width: 1024px) and (min-width: 768px) {
//                     min-width: 80%;
//                 }
//             }
//         `,
//         modalBody: css`
//             width: auto;
//             max-width: initial;
//             max-height: auto !important;
//             height: auto !important;
//             box-shadow: 0 0 10px 0 rgb(0 0 0 / 0.15) !important;
//             border: 1px solid #e5e5e5;
//             background-color: #fff;
//             position: absolute;
//             top: 290px;
//             ${({ theme }: { theme: BaseTheme }) =>
//                 breakpointMediaQueries(
//                     theme,
//                     [
//                         css`
//                             top: 1%;
//                             margin: 0 auto;
//                             min-width: 90% !important;
//                             width: 90% !important;
//                         `,
//                         css`
//                             top: 1%;
//                             margin: 0 auto;
//                             min-width: 90% !important;
//                             width: 90% !important;
//                         `,
//                         css`
//                             top: 1%;
//                             margin: 0 auto;
//                             min-width: 90% !important;
//                             width: 90% !important;
//                         `,
//                         css`
//                             top: 290px;
//                             min-width: auto !important;
//                             width: auto !important;
//                         `,
//                         css`
//                             top: 290px;
//                             min-width: auto !important;
//                             width: auto !important;
//                         `,
//                     ],
//                     "min",
//                 )}
//         `,
//         modalTitle: css`
//             border-bottom: none;
//             padding: 10px 10px 0;
//         `,
//         modalContent: css`
//             text-align: center;
//             display: flex;
//             flex-direction: column;
//             height: auto;
//         `,
//         titleButton: css`
//             color: #363636 !important;
//             border: none;
//             padding: 0;
//             > span {
//                 color: #363636 !important;
//                 height: 100%;
//                 width: 100%;
//                 > svg {
//                     height: 38px;
//                     width: 38px;
//                     stroke-width: 1;
//                     stroke: #363636;
//                 }
//             }
//             &:hover {
//                 background: none;
//             }
//         `,
//     },
// };

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    onImageClick,
    appState,
    fullPrimaryImageModalIsOpen: isModalOpen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;
    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    const selectedImage = getSelectedImage(appState, product);
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(isModalOpen);
    const [loading, setLoading] = React.useState(true);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const openModal = () => {
        setFullImageModalIsOpen(true);
    };

    const fullPrimaryImageModalCloseHandler = () => {
        if (onImageClick) {
            onImageClick();
        }
        setFullImageModalIsOpen(false);
    };

    React.useEffect(() => {
        if (loading) {
            setTimeout(() => {
                setLoading(false);
            }, 1800);
        }
    }, [loading]);

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images?.length === 0 || loading) {
        return null;
    }
    return (
        <>
            {!onImageClick && (
                <GridContainer id="imageCaraousel" {...styles.mainContainer} key={product.id}>
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer}>
                                <StyledWrapper {...styles.carouselSlidesContainer}>
                                    {product.images.slice(0, 4).map((productImage, index) => (
                                        <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                            <StyledWrapper {...styles.carouselSlideInner}>
                                                <Clickable onClick={() => imageClickHandler(index)}>
                                                    <LazyImage
                                                        {...(index === selectedImageIndex
                                                            ? styles.selectedImage
                                                            : styles.image)}
                                                        src={getProductImageThumbPath(productImage)}
                                                        altText={productImage.imageAltText}
                                                    />
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                    {product.images.length > 4 && (
                                        <StyledWrapper {...styles.carouselSlide} onClick={openModal}>
                                            <StyledWrapper {...styles.carouselSlideLast}>
                                                <StyledWrapper {...styles.lastSliderElement}>
                                                    <StyledWrapper {...styles.innerCircle}></StyledWrapper>
                                                    <StyledWrapper {...styles.innerCircle}></StyledWrapper>
                                                    <StyledWrapper {...styles.innerCircle}></StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            )}
            <Modal
                id="carousel_modal"
                {...primaryImageStyles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={primaryImageStyles.fullPrimaryImage}
                />
                <StyledWrapper {...styles.modalGridContainer}>
                    {product.images.map((productImage, index) => (
                        <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                            <StyledWrapper {...styles.modalCarouselSlideInner}>
                                <LazyImage
                                    {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                    src={getProductImageThumbPath(productImage)}
                                    altText={productImage.imageAltText}
                                    onClick={() => imageClickHandler(index)}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
